import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRightLeft, Loader2, CheckCircle2, Clock, XCircle, Package, Send, Truck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import { useOperationDraft } from '@/hooks/useOperationDraft';
import { DraftsMenu } from '@/components/DraftsMenu';
import { ResumedDraftBanner } from '@/components/ResumedDraftBanner';

import { EwbDocPanel } from '@/components/ewayBill/EwbDocPanel';
const ProcessingInternalTransfer = () => {
  const navigate = useNavigate();
  const [completedConsignments, setCompletedConsignments] = useState([]);
  const [outgoingTransfers, setOutgoingTransfers] = useState([]);
  const [pendingShipments, setPendingShipments] = useState([]);  // Transfers waiting for shipment confirmation
  const [sections, setSections] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');  // Start with pending shipments
  
  // Transfer modal state
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [selectedConsignment, setSelectedConsignment] = useState(null);
  const [selectedDestination, setSelectedDestination] = useState('');
  const [transferRemarks, setTransferRemarks] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [confirmingShipment, setConfirmingShipment] = useState(null);
  const [resumedDraft, setResumedDraft] = useState(null);

  const {
    drafts, activeDraftId, saving, lastSavedAt,
    autoSave, clearDraft, loadDraft, removeDraft, setActiveDraftId,
  } = useOperationDraft({ moduleKey: 'section:Processing', operationType: 'internal_transfer' });

  // Recover an in-progress "Create Outgoing Transfer" (consignment + destination
  // + remarks) if the operator closes/crashes before submitting.
  useEffect(() => {
    if (!showTransferModal || !selectedConsignment || !selectedDestination) return;
    autoSave({
      label: `${selectedConsignment.transfer_id} → ${selectedDestination}`,
      bundleCount: selectedConsignment.bundle_count || selectedConsignment.inventory_count || 1,
      documentId: selectedConsignment.transfer_id,
      documentNo: selectedConsignment.transfer_id,
      payload: {
        sourceTransferId: selectedConsignment.transfer_id,
        destination: selectedDestination,
        remarks: transferRemarks,
      },
    });
  }, [showTransferModal, selectedConsignment, selectedDestination, transferRemarks, autoSave]);

  const handleResumeDraft = async (draftId) => {
    try {
      const doc = await loadDraft(draftId);
      const p = doc.payload || {};
      const consignment = completedConsignments.find(c => c.transfer_id === p.sourceTransferId);
      if (!consignment) {
        toast.error('Source consignment no longer available');
        return;
      }
      setSelectedConsignment(consignment);
      setSelectedDestination(p.destination || '');
      setTransferRemarks(p.remarks || '');
      setShowTransferModal(true);
      const summary = (drafts || []).find((x) => x.draft_id === draftId) || null;
      setResumedDraft(summary);
      toast.success(summary?.last_updated_by_name ? `Resumed draft — last worked on by ${summary.last_updated_by_name}` : 'Draft restored');
    } catch {
      toast.error('Failed to restore draft');
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [completedRes, outgoingRes, pendingRes, sectionsRes] = await Promise.all([
        apiClient.get('/processing/completed-consignments'),
        apiClient.get('/processing/outgoing-transfers'),
        apiClient.get('/internal-transfer/shipment-requests?from_section=Processing'),
        apiClient.get('/internal-transfer/sections?exclude_section=Processing')
      ]);
      
      setCompletedConsignments(completedRes.data.consignments || []);
      setOutgoingTransfers(outgoingRes.data.transfers || []);
      setPendingShipments(pendingRes.data.transfers || []);
      setSections(sectionsRes.data.sections || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load transfers');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmShipment = async (transferId) => {
    setConfirmingShipment(transferId);
    try {
      await apiClient.post('/internal-transfer/confirm-shipment', {
        transfer_id: transferId
      });
      toast.success('Shipment confirmed successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm shipment');
    } finally {
      setConfirmingShipment(null);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      processing_completed: { color: 'bg-purple-100 text-purple-800', icon: CheckCircle2, label: 'Processing Completed' },
      fully_transferred: { color: 'bg-gray-100 text-gray-800', icon: CheckCircle2, label: 'Fully Transferred' },
      created: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Created' },
      requested: { color: 'bg-orange-100 text-orange-800', icon: Send, label: 'Requested' },
      shipped: { color: 'bg-blue-100 text-blue-800', icon: Truck, label: 'Shipped' },
      received: { color: 'bg-green-100 text-green-800', icon: CheckCircle2, label: 'Received' },
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Pending' },
      rejected: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Rejected' }
    };
    const cfg = config[status] || { color: 'bg-gray-100 text-gray-800', icon: Clock, label: status };
    const Icon = cfg.icon;
    return (
      <Badge className={`${cfg.color} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {cfg.label}
      </Badge>
    );
  };

  const handleCreateTransfer = (consignment) => {
    const existing = (drafts || []).find((d) => d.document_id === consignment.transfer_id);
    if (existing) { handleResumeDraft(existing.draft_id); return; }
    setResumedDraft(null);
    setSelectedConsignment(consignment);
    setSelectedDestination('');
    setTransferRemarks('');
    setActiveDraftId(null);
    setShowTransferModal(true);
  };

  const handleStartFresh = async () => {
    const id = resumedDraft?.draft_id;
    if (id) {
      const ok = await removeDraft(id);
      if (!ok) { toast.error('Could not discard the shared draft — please try again'); return; }
    }
    setResumedDraft(null);
    setShowTransferModal(false);
    setSelectedConsignment(null);
    setSelectedDestination('');
    setTransferRemarks('');
  };

  const submitTransfer = async () => {
    if (!selectedDestination) {
      toast.error('Please select a destination section');
      return;
    }
    
    setIsCreating(true);
    try {
      const response = await apiClient.post('/processing/create-outgoing-transfer', {
        source_transfer_id: selectedConsignment.transfer_id,
        destination_section: selectedDestination,
        remarks: transferRemarks.trim() || undefined,
      });
      
      toast.success(`Transfer ${response.data.transfer_id} created to ${selectedDestination}`);
      setShowTransferModal(false);
      setTransferRemarks('');
      clearDraft();
      setResumedDraft(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create transfer');
    } finally {
      setIsCreating(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#FFFBF5] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#2D5F4F]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/sections/processing')}
              className="text-white hover:bg-[#2D5F4F]"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold">Internal Transfers</h1>
              <p className="text-sm text-gray-400">Processing Section</p>
            </div>
          </div>
          <DraftsMenu
            drafts={drafts}
            onResume={handleResumeDraft}
            onDelete={removeDraft}
            activeDraftId={activeDraftId}
            saving={saving}
            lastSavedAt={lastSavedAt}
            variant="dark"
          />
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="pending" data-testid="pending-tab" className={pendingShipments.length > 0 ? "text-orange-600" : ""}>
              Pending Shipments ({pendingShipments.length})
            </TabsTrigger>
            <TabsTrigger value="completed" data-testid="completed-tab">
              Ready for Transfer ({completedConsignments.filter(c => c.status === 'processing_completed').length})
            </TabsTrigger>
            <TabsTrigger value="outgoing" data-testid="outgoing-tab">
              Outgoing Transfers ({outgoingTransfers.length})
            </TabsTrigger>
          </TabsList>

          {/* PENDING SHIPMENTS TAB */}
          <TabsContent value="pending">
            {pendingShipments.length === 0 ? (
              <Card className="bg-white">
                <CardContent className="p-8 text-center">
                  <Truck className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No pending shipment confirmations</p>
                  <p className="text-sm text-gray-400 mt-2">
                    Transfers that destinations have requested will appear here for shipment confirmation
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {pendingShipments.map((shipment) => (
                  <Card key={shipment.transfer_id} className="bg-white shadow-md border-orange-200" data-testid={`pending-${shipment.transfer_id}`}>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Clock className="w-5 h-5 text-orange-500" />
                          {shipment.transfer_id}
                        </CardTitle>
                        <Badge className="bg-orange-100 text-orange-700">Awaiting Shipment</Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-4 gap-4 mb-4">
                        <div>
                          <p className="text-xs text-gray-500">Destination</p>
                          <p className="font-medium text-purple-700">{shipment.to_section}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Bundles</p>
                          <p className="font-medium">{shipment.bundle_count || shipment.bundle_codes?.length || 0}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Requested At</p>
                          <p className="font-medium text-sm">
                            {shipment.requested_at ? new Date(shipment.requested_at).toLocaleString() : 'N/A'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Requested By</p>
                          <p className="font-medium text-sm">{shipment.requested_by_name || 'N/A'}</p>
                        </div>
                      </div>
                      <Button
                        onClick={() => handleConfirmShipment(shipment.transfer_id)}
                        disabled={confirmingShipment === shipment.transfer_id}
                        className="bg-green-600 hover:bg-green-700 text-white"
                        data-testid={`confirm-shipment-${shipment.transfer_id}`}
                      >
                        {confirmingShipment === shipment.transfer_id ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Confirming...
                          </>
                        ) : (
                          <>
                            <Truck className="w-4 h-4 mr-2" />
                            Confirm Shipment
                          </>
                        )}
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="completed">
            {completedConsignments.filter(c => c.status === 'processing_completed').length === 0 ? (
              <Card className="bg-white">
                <CardContent className="p-8 text-center">
                  <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No completed consignments ready for transfer</p>
                  <p className="text-sm text-gray-400 mt-2">
                    Complete processing on incoming consignments to create outgoing transfers
                  </p>
                  <Button
                    variant="link"
                    onClick={() => navigate('/sections/processing/inward-consignment')}
                    className="mt-2"
                  >
                    View Inward Consignments →
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {completedConsignments.filter(c => c.status === 'processing_completed').map((consignment) => (
                  <Card key={consignment.transfer_id} className="bg-white shadow-md" data-testid={`completed-${consignment.transfer_id}`}>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg">{consignment.transfer_id}</CardTitle>
                        {getStatusBadge(consignment.status)}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-4 gap-4 mb-4">
                        <div>
                          <p className="text-xs text-gray-500">Original Source</p>
                          <p className="font-medium">{consignment.from_section}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Processing Unit</p>
                          <p className="font-medium text-sm">{consignment.processing_unit_name || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Bundles</p>
                          <p className="font-medium">{consignment.bundle_count || consignment.inventory_count}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Completed</p>
                          <p className="font-medium text-sm">
                            {consignment.processing_completed_at 
                              ? new Date(consignment.processing_completed_at).toLocaleDateString()
                              : 'N/A'}
                          </p>
                        </div>
                      </div>
                      
                      {/* Show USN info if available */}
                      {consignment.usn_info && consignment.usn_info.length > 0 && (
                        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                          <p className="text-xs text-gray-500 mb-2">USN Information</p>
                          <div className="flex flex-wrap gap-2">
                            {consignment.usn_info.slice(0, 5).map((usn, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs">
                                {usn.bundle_barcode}: {usn.usn_code || 'No USN'}
                              </Badge>
                            ))}
                            {consignment.usn_info.length > 5 && (
                              <Badge variant="outline" className="text-xs">
                                +{consignment.usn_info.length - 5} more
                              </Badge>
                            )}
                          </div>
                        </div>
                      )}
                      
                      <Button
                        size="sm"
                        onClick={() => handleCreateTransfer(consignment)}
                        className="bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
                        data-testid={`transfer-btn-${consignment.transfer_id}`}
                      >
                        <Send className="w-4 h-4 mr-1" />
                        Create Outgoing Transfer
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="outgoing">
            {outgoingTransfers.length === 0 ? (
              <Card className="bg-white">
                <CardContent className="p-8 text-center">
                  <ArrowRightLeft className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No outgoing transfers</p>
                  <p className="text-sm text-gray-400 mt-2">
                    Create transfers from completed processing consignments
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {outgoingTransfers.map((transfer) => (
                  <Card key={transfer.transfer_id} className="bg-white shadow-md" data-testid={`outgoing-${transfer.transfer_id}`}>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg">{transfer.transfer_id}</CardTitle>
                        {getStatusBadge(transfer.status)}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-4 gap-4">
                        <div>
                          <p className="text-xs text-gray-500">Destination</p>
                          <p className="font-medium">{transfer.to_section}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Bundles</p>
                          <p className="font-medium">{transfer.bundle_count}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Created</p>
                          <p className="font-medium text-sm">{new Date(transfer.created_at).toLocaleDateString()}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Source Transfer</p>
                          <p className="font-medium text-sm">{transfer.source_transfer_id || 'Direct'}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
        <div className="mt-6"><EwbDocPanel docType="branch_transfer" section="Processing" title="Internal transfer e-way bills (goods leaving the site)" /></div>
      </main>

      {/* Transfer Modal */}
      <Dialog open={showTransferModal} onOpenChange={setShowTransferModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create Outgoing Transfer</DialogTitle>
          </DialogHeader>
          
          {selectedConsignment && (
            <div className="space-y-4">
              {resumedDraft && (
                <ResumedDraftBanner draft={resumedDraft} onStartFresh={handleStartFresh} />
              )}
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Source Consignment</p>
                <p className="font-medium">{selectedConsignment.transfer_id}</p>
                <p className="text-sm text-gray-600 mt-1">
                  {selectedConsignment.bundle_count || selectedConsignment.inventory_count} bundles from {selectedConsignment.from_section}
                </p>
              </div>
              
              <div>
                <Label>Destination Section</Label>
                <Select value={selectedDestination} onValueChange={setSelectedDestination}>
                  <SelectTrigger className="mt-1" data-testid="destination-select">
                    <SelectValue placeholder="Select destination section" />
                  </SelectTrigger>
                  <SelectContent>
                    {sections.map((section) => (
                      <SelectItem 
                        key={section.section_name} 
                        value={section.section_name}
                      >
                        {section.section_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Remarks (Optional)</Label>
                <Textarea
                  value={transferRemarks}
                  onChange={(e) => setTransferRemarks(e.target.value)}
                  placeholder="e.g. urgent for shipment, handle this lot first…"
                  rows={2}
                  className="mt-1"
                  data-testid="remarks-input"
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTransferModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={submitTransfer} 
              disabled={isCreating || !selectedDestination}
              className="bg-[#2D5F4F] hover:bg-[#234A3D]"
              data-testid="submit-transfer-btn"
            >
              {isCreating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
              Create Transfer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProcessingInternalTransfer;
